import { TextInputProps } from 'types/ui'
import { InputContainer, useInputClassname } from './input-container'
import { ChangeEvent, ChangeEventHandler, FocusEventHandler, useCallback } from 'react'

export function TextInput({
  id,
  error,
  warning,
  success,
  as: Component = 'input',
  type = 'text',
  disabled,
  placeholder,
  leftIcon: LeftIcon,
  onChange,
  rows = 5,
  value,
  className,
  ...props
}: TextInputProps) {
  const regular = Component === 'input'
  const hasPrefix = !!(regular && LeftIcon)

  const inputClassname = useInputClassname({
    className,
    success,
    warning,
    error,
    textarea: Component === 'textarea',
    hasPrefix,
    hasValue: !!value,
    isDate: type === 'date',
    regular,
  })

  const handleChange = useCallback<ChangeEventHandler>(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange(e.target.value)
    },
    [onChange]
  )

  const handleBlur = useCallback<FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>>(
    (e) => {
      props.onBlur?.(e)
    },
    [props]
  )

  return (
    <InputContainer
      disabled={disabled}
      error={error}
      warning={warning}
      success={success}
      leftIcon={LeftIcon}
      regular={regular}
    >
      <Component
        {...props}
        value={value ?? ''}
        onChange={handleChange}
        onBlur={handleBlur}
        rows={Component === 'textarea' ? rows : undefined}
        role="textbox"
        data-testid="text-input"
        className={inputClassname}
        id={id}
        type={type}
        disabled={disabled}
        aria-disabled={disabled}
        placeholder={placeholder}
      />
    </InputContainer>
  )
}
